import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  GraduationCap, FileText, CheckCircle2, ArrowRight, Calendar, 
  Clock, DollarSign, Users, Mail, Phone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BackNavigation from "@/components/BackNavigation";
import { useCMSContent } from "@/hooks/useCMSContent";

// Cinematic visual assets - 4K UHD Human-Centered (PROTECTED - DO NOT MODIFY)
const CINEMATIC_VISUALS = {
  heroVideo: "https://videos.pexels.com/video-files/3196487/3196487-uhd_2560_1440_25fps.mp4",
  heroPoster: "https://images.pexels.com/photos/5439143/pexels-photo-5439143.jpeg?w=1920"
};

// Fallback content for CMS
const FALLBACK_CONTENT = {
  hero: {
    badge: "Join Zodiac",
    title: "Admissions",
    description: "Begin your journey toward workforce intelligence mastery. Explore our admission requirements and apply to our programs."
  }
};

export default function Admissions() {
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  // Fetch CMS content with fallback
  const { content } = useCMSContent('admissions', FALLBACK_CONTENT);
  const hero = content.hero || FALLBACK_CONTENT.hero;
  
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = 0.6;
      const handleLoaded = () => setVideoLoaded(true);
      video.addEventListener('loadeddata', handleLoaded);
      return () => video.removeEventListener('loadeddata', handleLoaded);
    }
  }, []);

  const steps = [
    {
      number: "01",
      title: "Submit Application",
      description: "Complete the online application form with your professional background and program selection."
    },
    {
      number: "02",
      title: "Document Review",
      description: "Our admissions team reviews your credentials, experience, and application materials."
    },
    {
      number: "03",
      title: "Interview",
      description: "Selected candidates participate in an admissions interview with program faculty."
    },
    {
      number: "04",
      title: "Decision",
      description: "Receive your admission decision and enrollment package within 2-3 weeks."
    },
    {
      number: "05",
      title: "Enrollment",
      description: "Complete enrollment, payment, and onboarding to begin your program."
    }
  ];

  const requirements = [
    "Bachelor's degree from an accredited institution (for graduate programs)",
    "Minimum 3 years professional experience (for executive programs)",
    "Current resume or CV",
    "Statement of purpose",
    "Professional references (2)",
    "English proficiency (for non-native speakers)"
  ];

  const tuitionInfo = [
    { program: "Executive Professional Masters", range: "$25,000 - $45,000", duration: "12-18 months" },
    { program: "Training Certificates", range: "$2,500 - $8,500", duration: "3-6 months" },
    { program: "Individual Courses", range: "$495 - $1,995", duration: "4-12 weeks" },
    { program: "Licensing Programs", range: "Contact for pricing", duration: "Varies" }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admissions-page">
      {/* Back Navigation */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero - Cinematic Video Background */}
      <section className="relative overflow-hidden min-h-[60vh] flex items-center -mt-12">
        {/* Video Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{ 
              backgroundImage: `url(${CINEMATIC_VISUALS.heroPoster})`,
              opacity: videoLoaded ? 0 : 1
            }}
          />
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            poster={CINEMATIC_VISUALS.heroPoster}
          >
            <source src={CINEMATIC_VISUALS.heroVideo} type="video/mp4" />
          </video>
          {/* Gradient Overlay - 30% opacity */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A8A]/35 via-[#1E40AF]/30 to-[#1E3A8A]/25" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium mb-6" data-testid="admissions-hero-badge">
                <GraduationCap className="w-4 h-4" />
                {hero.badge}
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6 text-white" data-testid="admissions-hero-title">
                {hero.title}
              </h1>
              <p className="text-lg text-white/80 leading-relaxed mb-8" data-testid="admissions-hero-description">
                {hero.description}
              </p>
              <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90 h-12 px-8">
                Apply Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card className="border-[#E5E7EB] bg-white">
                <CardContent className="p-6 text-center">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-[#1E3A8A]" />
                  <p className="text-sm text-[#1E3A8A]">Rolling Admissions</p>
                  <p className="font-semibold" style={{ color: '#991B1B' }}>Year-Round</p>
                </CardContent>
              </Card>
              <Card className="border-[#E5E7EB] bg-white">
                <CardContent className="p-6 text-center">
                  <Clock className="w-8 h-8 mx-auto mb-2 text-[#1E3A8A]" />
                  <p className="text-sm text-[#1E3A8A]">Review Time</p>
                  <p className="font-semibold" style={{ color: '#991B1B' }}>2-3 Weeks</p>
                </CardContent>
              </Card>
              <Card className="border-[#E5E7EB] bg-white">
                <CardContent className="p-6 text-center">
                  <Users className="w-8 h-8 mx-auto mb-2 text-[#1E3A8A]" />
                  <p className="text-sm text-[#1E3A8A]">Support</p>
                  <p className="font-semibold" style={{ color: '#991B1B' }}>Dedicated Team</p>
                </CardContent>
              </Card>
              <Card className="border-[#E5E7EB] bg-white">
                <CardContent className="p-6 text-center">
                  <DollarSign className="w-8 h-8 mx-auto mb-2 text-[#1E3A8A]" />
                  <p className="text-sm text-[#1E3A8A]">Aid Available</p>
                  <p className="font-semibold" style={{ color: '#991B1B' }}>Scholarships</p>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Application Steps */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Application Process
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Follow these steps to complete your application to Zodiac Workforce Intelligence Institute.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-4">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] bg-white">
                  <CardContent className="p-5">
                    <span className="font-mono text-3xl font-bold text-[#E5E7EB]">{step.number}</span>
                    <h3 className="font-heading text-base font-semibold mt-3 mb-2" style={{ color: '#991B1B' }}>
                      {step.title}
                    </h3>
                    <p className="text-xs text-[#1E3A8A]">{step.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl font-bold mb-6" style={{ color: '#991B1B' }}>
                Eligibility Requirements
              </h2>
              <div className="space-y-4">
                {requirements.map((req, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
                    <span className="text-[#1E3A8A]">{req}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl font-bold mb-6" style={{ color: '#991B1B' }}>
                Tuition Information
              </h2>
              <div className="space-y-4">
                {tuitionInfo.map((item, index) => (
                  <Card key={index} className="border-[#E5E7EB]">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-[#1E3A8A]">{item.program}</p>
                          <p className="text-xs text-[#1E3A8A]/60">{item.duration}</p>
                        </div>
                        <p className="font-mono font-semibold" style={{ color: '#991B1B' }}>{item.range}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-[#1E3A8A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold mb-4">
            Admissions Contact
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Have questions? Our admissions team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-8">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              <span>admissions@zodiac-institute.edu</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              <span>+1 (555) 123-4567</span>
            </div>
          </div>
          <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90 h-12 px-8">
            Apply Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}
